import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const hidden = {
  up: css`
    opacity: 0;
    transform: translateY(42px);
  `,
  left: css`
    opacity: 0;
    transform: translateX(-52px);
  `,
  right: css`
    opacity: 0;
    transform: translateX(52px);
  `,
  scale: css`
    opacity: 0;
    transform: scale(0.88);
  `
};

const RevealBox = styled.div`
  transition: opacity 850ms ${EASE} ${({ $delay }) => $delay}ms,
    transform 850ms ${EASE} ${({ $delay }) => $delay}ms;
  will-change: opacity, transform;

  ${({ $visible, $variant }) =>
    $visible
      ? css`
          opacity: 1;
          transform: none;
        `
      : hidden[$variant] || hidden.up}

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.001ms;
  }
`;

/**
 * Revela su contenido cuando entra al viewport.
 * `variant`: 'up' (default) | 'left' | 'right' | 'scale'
 * `delay` en ms para escalonar elementos consecutivos.
 */
function Reveal({ children, delay = 0, variant = 'up', ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.14, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <RevealBox ref={ref} $visible={visible} $delay={delay} $variant={variant} {...rest}>
      {children}
    </RevealBox>
  );
}

export default Reveal;
